import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { Container, PageHeader, Section, ActionBar } from '@vnetwork/ui/layout';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreatePolicy, s3ExtractError } from '@/api/s3';
import { JsonEditor } from '@/components/JsonEditor';
import { POLICY_TEMPLATES, type PolicyTemplate } from './policy-templates';

// IAM policy names allow word chars plus + = , . @ - ; mirror the backend rule
// so the user gets the error before the round-trip.
const NAME_RE = /^[\w+=,.@-]{1,128}$/;

/** Create an IAM managed policy — name + JSON document, validated client-side. */
export function PolicyCreate({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreatePolicy(serviceId);

  const [name, setName] = useState('');
  const [doc, setDoc] = useState('');
  const [touched, setTouched] = useState(false);

  // Quick templates: prefill the editor with a ready-made policy document (and a
  // suggested name if the field is still blank), then let the user tweak it.
  const applyTemplate = (t: PolicyTemplate) => {
    setDoc(t.document);
    setName((n) => (n.trim() ? n : t.suggestedName));
    setTouched(false);
  };

  const nameError =
    touched && !NAME_RE.test(name)
      ? 'Use 1–128 chars: letters, digits or + = , . @ - _'
      : null;

  const trimmed = doc.trim();
  let docError: string | null = null;
  if (touched) {
    if (!trimmed) docError = 'Policy document is required.';
    else {
      try {
        JSON.parse(trimmed);
      } catch {
        docError = 'Not valid JSON.';
      }
    }
  }

  // Pretty-print the current document (2-space indent) so it reads like an
  // editor; no-op with a toast when the JSON is invalid.
  const formatDoc = () => {
    try {
      setDoc(JSON.stringify(JSON.parse(doc), null, 2));
    } catch {
      toast.error('Not valid JSON — fix errors before formatting');
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!NAME_RE.test(name) || !trimmed) return;
    try {
      JSON.parse(trimmed);
    } catch {
      return;
    }
    create.mutate(
      { policy_name: name, document: trimmed },
      {
        onSuccess: () => {
          toast.success('Policy created');
          navigate(`/services/oss/${serviceId}/policies`);
        },
        onError: (err) => toast.error(s3ExtractError(err, 'Failed to create policy')),
      },
    );
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<ScrollText size={22} />}
        title="Create policy"
        description="Define an IAM managed policy with a JSON document."
      />
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section title="Policy" style={{ marginBottom: 0 }}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field
              label="Quick templates"
              description="Prefill a common policy, then tweak the document below."
            >
              {() => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                  {POLICY_TEMPLATES.map((t) => (
                    <Button
                      key={t.id}
                      type="button"
                      size="sm"
                      variant="secondary"
                      title={t.description}
                      onClick={() => applyTemplate(t)}
                    >
                      {t.label}
                    </Button>
                  ))}
                </div>
              )}
            </Field>

            <div style={{ maxWidth: 720, display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Field label="Name" required error={nameError ?? undefined}>
                {(p) => (
                  <Input
                    {...p}
                    placeholder="e.g. read-only-media"
                    value={name}
                    invalid={!!nameError}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => setTouched(true)}
                  />
                )}
              </Field>
            </div>

            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" disabled={!trimmed} onClick={formatDoc}>
                  Format JSON
                </Button>
              </div>
              <Field label="Policy document (JSON)" required error={docError ?? undefined}>
                {() => (
                  <JsonEditor
                    value={doc}
                    onChange={setDoc}
                    invalid={!!docError}
                    ariaLabel="Policy document JSON"
                  />
                )}
              </Field>
            </div>
          </div>
        </Section>

        <ActionBar
          start={
            <Button
              type="button"
              variant="secondary"
              disabled={create.isPending}
              onClick={() => navigate(`/services/oss/${serviceId}/policies`)}
            >
              Cancel
            </Button>
          }
          end={
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create policy'}
            </Button>
          }
        />
      </form>
    </Container>
  );
}
